//***********************************
// マスター　会社情報の型定義
//***********************************
export interface Company {
  id: string; // Firestoreが自動生成するドキュメントID
  companyID: string; // 4桁の会社ID
  name: string;
  address?: string;
  phone?: string;
  person?: string;
  firstDate: number; // 初回訪問日 (Unixタイムスタンプ long)
  startDate: number; // 契約開始日 (Unixタイムスタンプ long)
  createdDate: number; // 作成日 (Unixタイムスタンプ long)
  remarks?: string;
  isDeleted?: boolean; // 論理削除フラグ
}

//***********************************
// マスター　組織情報の型定義
//***********************************
export interface Organization {
  id: string; // Firestoreが自動生成するドキュメントID
  orgID: string; // 会社ID(4桁) + 組織固有ID(3桁) = 7桁の文字列
  name: string;
  parentOrgID: string; // 親組織のorgID。ルート組織の場合は companyID + "000"
  parentOrgName?: string; // 親組織の名前
  companyID: string; // 所属する会社のcompanyID
  createdDate: number; // 作成日 (Unixタイムスタンプ)
  Others?: string;
}

//***********************************
// マスター　担当者情報の型定義
//***********************************
export interface Person {
  id: string; // Firestoreが自動生成するドキュメントID
  personID: string; // 担当者ID (4桁の文字列)
  name: string;
  phone?: string;
  email1: string;
  email2?: string;
  orgID?: string; // 将来用
  orgName?: string; // 将来用
  companyID: string; // 所属する会社のcompanyID
  createdDate: number; // 作成日 (Unixタイムスタンプ)
  Others?: string;
}

//***********************************
// マスター　ログインユーザー管理用
//***********************************
export const ADMIN_ROLE = "admin" as const;
export const STANDARD_ROLE = "standard" as const;
export type UserRole = typeof ADMIN_ROLE | typeof STANDARD_ROLE;

export interface LoginUser {
  id: string;                    // ドキュメントID（Firebase AuthのUID）
  userName?: string;             // 旧データ互換（新規保存では使用しない）
  userId?: string;               // ユーザーID（業務識別子）
  displayName?: string;          // ユーザー名
  email: string;                 // メールアドレス
  role: UserRole;                // ユーザーロール
  createdAt: number;             // 作成日時（UnixTimeStamp）
  updatedAt?: number;            // 更新日時
  lastLoginAt?: number | null;   // 最終ログイン日時（UnixTimeStamp）
  lastLogin?: string;            // 旧フィールド互換
  companyId?: string;            // 旧データ互換（新規保存では使用しない）
  orgID?: string;                // 旧データ互換（新規保存では使用しない）
  orgName?: string;              // 旧データ互換（新規保存では使用しない）
  departmentId?: string;         // 旧フィールド互換
  phoneNumber?: string;          // 電話番号
  Others?: string;              // 備考
}
export interface CreateUserRequest {
  userId?: string;
  displayName?: string;
  email: string;
  password: string;
  role: UserRole;
  phoneNumber?: string;
  Others?: string;
}
export interface UpdateUserRequest {
  userId?: string;
  displayName?: string;
  role?: UserRole;
  lastLoginAt?: number | null;
  phoneNumber?: string;
  Others?: string;
}

export interface Contract {
  id: string;                    // 履歴ドキュメントID（自動採番）
  userId: string;                // Auth UID
  email: string;                 // 管理者メール
  displayName: string;           // 表示名
  deviceId: string;              // そのセットアップ時のデバイスID
  setupCount: number;            // 同一メールでの何回目のセットアップか
  lastSetupAt: number;           // セットアップ日時
  createdAt: number;             // レコード作成日時
  /** 同一メール（または userId）のうち最新の履歴のみ true */
  isActive?: boolean;
}

export interface UserInfo {
  // userID が必須なら (string)
  // あるいは任意なら (string | undefined)
  // 必須かオプションか、プロジェクト方針にあわせて統一してください
  userID: string;
  userName?: string;
}


//***********************************
// マスター　デバイス情報
//***********************************
export interface Device {
  id: string; // Document ID = deviceId（Firestore）
  serialNumber: string;
  deviceName?: string;
  manufacturer?: string;
  model?: string;
  osVersion?: string;
  appVersion?: string;
  versionCode?: number;
  versionName?: string;
  /** 直近の起動成功時刻（ms） */
  lastSeenAt?: number;
  crashCount?: number;
  lastCrashAt?: number;
  lastCrashSummary?: string;
  lastCrashVersionCode?: number;
  networkStatus?: string;
  telNo?: string;
  lastSyncTime?: number;
  updateTime?: number;
  isDeleted: boolean;

  // --- Firestore DeviceRecord の認証・ユーザー情報 ---
  userId?: string;
  authProvider?: string;
  userEmail?: string | null;
  userDisplayName?: string | null;
  userPhotoUrl?: string | null;
  managerEmail?: string | null;
  familyEmails?: string[];
  userInfo?: {
    userID: string;
    userName: string;
  };
  fcmToken?: string;

  // --- プラン・有効期限・会話時間（課金・AI通話制限） ---
  /** プラン（free: お試し / standard_plan: サブスク） */
  plan?: "free" | "standard_plan";
  /** サブスク有効期限（ミリ秒タイムスタンプ） */
  expiry?: number;
  /** AI通話の残り時間（数値） */
  conversationTimeRemaining?: number;
  /** 月次chat_timeから集計した全期間累計の会話時間（ms） */
  totalChatTimeMs?: number;
  /** 全期間累計の最終集計日時（ms） */
  totalChatTimeUpdatedAt?: number;
  /** チャット警告時間（分）。0 は警告なし */
  chat_warn?: number;
  /** チャット上限時間（分）。0 は上限なし */
  chat_limit?: number;
}

//***********************************
// お知らせ（ケアセンター、担当者）
//***********************************
export interface Oshirase {
  id: string;
  createTime: number;  // 文字列から数値型に変更
  title: string;       // タイトル名
  message: string;
  flgKidoku: boolean;
  kidokuTime: number;  // 既読時間を追加
  serialNumber: string;
  regPerson: string;       // "CENTER/USER_ユーザー名"の形式でデータを格納  例:CENTER_鈴木　USER_山田など
  
}

//***********************************
// メモ
//***********************************
export interface Memo {
  id: string;             // 自動で追加されるID
  memo: string;           // メモの内容
  priority: number;       // 1-3
  serialNumber: string;   // シリアル番号
  title: string;          // "CENTER/USER_ユーザー名"の形式でデータを格納  例:CENTER_鈴木　USER_山田など
  updateTime: string;     // 更新時間

}

//***********************************
// カレンダー
//***********************************
export interface Calendar {
  id: string;             // 自動で追加されるID
  title: string;          // "CENTER/USER_ユーザー名"の形式でデータを格納  例:CENTER_鈴木　USER_山田など
  description: string;    // コンテンツ
  popupbefore: number;    // ○○分前にポップアップ
  eventTime: string;      // カレンダーの時間
  updateTime: string;     // 更新時間
  serialNumber: string;   // シリアル番号
 
}

//***********************************
// リンク(Web/Youtube共通)
//***********************************
export interface Link {
  id: string;             // 自動で追加されるID
  mode: number;           // モード  1:お役立ち（Webプリセット） 2:お気に入り（ユーザー登録）リンク 3:おすすめ（定期更新）4:お役立ち（Youtubeプリセット） 5:お気に入り（Youtube）リンク 6:おすすめ（Youtube）
  title: string;          // リンクのタイトル
  url: string;            // URL
  description: string;    // リンクの説明文
  updateTime: number;     // 更新時間
  serialNumber: string;   // シリアル番号
 
}


//***********************************
// おくすりタイマー
//***********************************
export interface Okusuri {
  id: string;             // 自動で追加されるID
  title: string;          // "CENTER/USER_ユーザー名"の形式でデータを格納  例:CENTER_鈴木　USER_山田など
  timername: string;      // タイマー名
  nexttaketime: string;   // 次回の服用時間
  takeflag: number;       // 0:未服用 1:服用済み 2:スキップ
  cfg_mode: number;       // モード 1:毎日　2:毎週 3:毎月 4:定期(〇日に1回)
  cfg_day: number;        // 1-31 (モード3、4のみ使用) モード4の時は開始オフセット0～5
  cfg_week: number;       // 1:月 2:火 3:水 4:木 5:金 6:土 7:日 (モード2のみ使用)
  cfg_num: number;        // 2-10 (モード4のみ使用 〇日に1回)
  cfg_taketime: string;   // "99:99" 飲む時間をセット　（文字列）
  alarmSound: number;     // アラームの音　
  updateTime: string;     // 更新時間
  serialNumber: string;   // シリアル番号
  
}

//***********************************
// Zoom会議
//***********************************
export interface Zoom {
  id: string;              // 自動で追加されるID
  meetingName: string;     // 会議名
  starttime: string;       // 開始時間(long値)
  durationminute: number;  // 会議時間 (分)
  meetingID: string;       // ミーティングID
  passCode: string;        // パスコード、入室時に必要
  name: string;            // 参加するときの名前
  mode: number;            // モード 1:オリジナル 2:カスタム
  deleted: boolean;        // 削除済みフラグ
  updateTime: string;      // 最終更新時間
  serialNumber: string;    // シリアル番号 共通で持たせる
  regPerson: string;       // "CENTER/USER_ユーザー名"の形式でデータを格納  例:CENTER_鈴木　USER_山田など

}

// Zoom会議用 自作のZoom会議室
export interface MeetingInfo {    
  meetingNumber: string;    // ミーティングID
  password: string;         // パス
  userName: string;         // 入室時のユーザー名
}

export interface ZoomMimamori {
  id: string;              // 自動で追加されるID
  meetingName: string;     // 会議名
  starttime: string;       // 開始時間(long値)
  durationminute: number;  // 会議時間 (分)
  meetingID: string;       // ミーティングID
  passCode: string;        // パスコード、入室時に必要
  name: string;            // 参加するときの名前
  mode: number;            // モード 1:オリジナル 2:カスタム
  deleted: boolean;        // 削除済みフラグ
  updateTime: string;      // 最終更新時間
  serialNumber: string;    // シリアル番号 共通で持たせる
  regPerson: string;       // "CENTER/USER_ユーザー名"の形式でデータを格納  例:CENTER_鈴木　USER_山田など
  
  emergencyID: string;     // 緊急会議ID：緊急呼び出しの識別用（会議が開催済みか否か識別）
  limitTime: string;       // 利用可能期限 この時間まで利用可能な会議室
  flgOpen: number;         // 会議中フラグ 0:未開催 1:開催中
}

//***********************************
// Agora会議
//***********************************
export interface Agora {
  id: string;              // 自動で追加されるID
  meetingName: string;     // 会議名
  starttime: string;       // 開始時間(long値)
  durationminute: number;  // 会議時間 (分)
  channelID: string;       // ChannelID 自分で決めるUUID
  updateTime: string;      // 最終更新時間
  uid: number;             // 会議参加時のユーザーID 1,2,3...
  serialNumber: string;    // シリアル番号 共通で持たせる
  name: string;            // 参加するときの名前
  regPerson: string;       // "USER" or CARECENTER ユーザーによる登録の場合 それ以外は文字列が入っている。（"ケアセンターによる登録"など）
  deleted: boolean;        // 削除済みフラグ

  ictabUid: string;        // 通常会議のみ: Care Tablet WebアプリにログインしているユーザーのログインユーザーID
}

export interface AgoraMimamori {
  id: string;              // 自動で追加されるID
  meetingName: string;     // 会議名
  starttime: string;       // 開始時間(long値)
  durationminute: number;  // 会議時間 (分)
  channelID: string;       // ChannelID 自分で決めるUUID
  updateTime: string;      // 最終更新時間
  uid: number;             // 会議参加時のユーザーID 1,2,3...

  serialNumber: string;    // シリアル番号 共通で持たせる
  name: string;            // 参加するときの名前
  regPerson: string;       // "CENTER/USER_ユーザー名"の形式でデータを格納  例:CENTER_鈴木　USER_山田など
  deleted: boolean;        // 削除済みフラグ //微妙一応残す
  
  emergencyID: string;     // 緊急会議ID：緊急呼び出しの識別用（会議が開催済みか否か識別）
  limitTime: string;       // 利用可能期限 この時間まで利用可能な会議室
  flgOpen: number;         // 会議中フラグ 0:未開催 1:開催中

}

//***********************************
// 緊急みまもり会議情報の型定義
//***********************************
export interface AgoraMimamoriMeeting {
  docId?: string;            // ドキュメントID
  channelID: string;         // Agoraチャネルid
  meetingName: string;       // 会議名
  starttime: number;         // 開始時間(タイムスタンプ)
  durationminute: number;    // 会議時間(分)
  serialNumber: string;      // デバイスのシリアル番号
  regPerson: string;         // 登録者名
  name: string;              // 表示名
  uid: number;               // ユーザーID
  deleted: boolean;          // 削除フラグ
  updateTime: number;        // 更新時間

  // 緊急会議用の拡張
  limitTime: number;         // 有効期限(タイムスタンプ)
  emergencyID: string;       // 緊急ID
  flgOpen: number;           // 開催フラグ (0: 未開催, 1: 開催中)
  
  // 表示用の追加情報（取得後に追加されるフィールド）
  deviceName?: string;       // デバイス名
  userName?: string;         // ユーザー名
  alreadyStarted?: boolean;  // すでに開始されているかどうか
}

//***********************************
// 掲示板用の型定義を既存のtypes.tsに追加
//***********************************
// 掲示板ユーザー
export interface BBSUser {
  id: string;                    // ドキュメントID
  serialNumber: string;          // 対象デバイスのシリアル番号
  email: string;                 // 通知先メールアドレス
  displayName: string;           // 表示名
  userType: 'device_owner' | 'family' | 'general'; // ユーザー種別
  enableEmailNotification: boolean; // メール通知有効/無効
  isActive: boolean;             // アクティブ状態
  createdAt: number;             // 登録日時（UnixTimeStamp）
  updatedAt: number;             // 更新日時（UnixTimeStamp）
  createdBy: string;             // 登録者ID（LoginUserのID）
}

// 既読情報（投稿内の配列要素として使用）
export interface BBSPostRead {
  userEmail: string;             // 既読ユーザーのメールアドレス
  userName: string;              // 既読ユーザーの表示名
  userType: 'device_owner' | 'family' | 'general'; // ユーザー種別
  readAt: number;                // 既読日時（UnixTimeStamp）
}

// 掲示板投稿（新方式：readBy配列を含む）
export interface BBSPost {
  id: string;                    // ドキュメントID
  serialNumber: string;          // 対象デバイスのシリアル番号
  authorId: string;              // 投稿者ID（BBSUserのID）
  authorName: string;            // 投稿者名  
  authorType: 'device_owner' | 'family' | 'general'; // 投稿者種別
  content: string;               // 投稿内容（最大1000文字）
  isEmailSent: boolean;          // メール通知が送信されたかどうか
  createdAt: number;             // 投稿日時（UnixTimeStamp）
  isDeleted: boolean;            // 削除フラグ
  readBy?: BBSPostRead[];        // 既読情報の配列（新方式）
  source?: string;               // 投稿元（'web' | 'android'）
  imageUrl?: string;             // 投稿画像のURL
  mediaType?: 'image' | 'video' | null; // ★ 追加: メディアの種類を識別
}

// 投稿表示用の拡張型（互換性維持のため）
export interface BBSPostWithReads extends BBSPost {
  reads?: BBSPostRead[];         // 既読情報の配列（旧互換性用）
  readCount?: number;            // 既読者数（投稿者除く）
  isReadByCurrentUser?: boolean; // 現在のユーザーが既読済みか
}

// 掲示板設定（デバイスごと）
export interface BBSSettings {
  id: string;                    // ドキュメントID（serialNumberと同じ）
  serialNumber: string;          // 対象デバイスのシリアル番号
  boardName: string;             // 掲示板名
  description: string;           // 掲示板の説明
  isActive: boolean;             // 掲示板有効/無効
  maxDisplayPosts: number;       // 最大表示投稿数（デフォルト10）
  createdAt: number;             // 作成日時
  updatedAt: number;             // 更新日時
  createdBy: string;             // 作成者ID
}

// 表示用の拡張投稿情報
export interface BBSPostWithDetails extends BBSPost {
  deviceName?: string;           // デバイス名（表示用）
  userName?: string;             // デバイスユーザー名（表示用）
  relativeTime?: string;         // 相対時間表示用（「2時間前」など）
}

// 掲示板ユーザー管理用のフォームデータ
export interface BBSUserFormData {
  email: string;
  displayName: string;
  userType: 'device_owner' | 'family' | 'general';
  enableEmailNotification: boolean;
}

// 投稿フォームデータ
export interface BBSPostFormData {
  content: string;
  sendEmailNotification: boolean;
}



//***********************************
// 遊び問題マスタ（play_questions）
//***********************************
export type PlayQuestionType = "riddle" | "quiz";

export interface PlayQuestion {
  id: string;
  type: PlayQuestionType;
  question: string;
  answer: string;
  hint: string;
  choice1: string;
  choice2: string;
  choice3: string;
  correctIndex: number; // quiz: 1..3 / riddle: 0
  genre: string;
  enabled: boolean;
  sortOrder: number;
}

export interface PlayQuestionFormValues {
  id?: string;
  question: string;
  answer: string;
  hint?: string;
  choice1?: string;
  choice2?: string;
  choice3?: string;
  correctIndex?: number;
  genre?: string;
  enabled?: boolean;
  sortOrder?: number;
}

//***********************************
// 旬おすすめ共通マスタ（linkitems_recommend）
//***********************************
export type LinkRecommendKind = "web" | "youtube";

export interface LinkRecommendItem {
  /** 編集UI用の一時ID */
  localId: string;
  title: string;
  description: string;
  /** Web: URL / YouTube: 検索キーワード */
  url: string;
  sortOrder: number;
}

export interface LinkRecommendFormValues {
  title: string;
  description: string;
  url: string;
}